import { CaseClient, EvidenceClient, EvidenceDetailsResponseWithUrls, EvidenceMarkerModel } from '@app/api2/api';
import moment from 'moment';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Grid, GridColumn, Header, Message } from 'semantic-ui-react';
import { Button, ButtonGroup, Container, Menu } from 'semantic-ui-react';
import { buildUrl, AppRoutes } from '../AppRoutes';
import { areYouSure } from '../AreYouSure';
import { toAlleleFormatName } from '@common/AlleleFormatNames';
import { BPPlotly } from '@common/BPPlotly';
import { BPColumn, BPReactTable } from '@common/BPReactTable';
import { BPSection } from '@common/BPSection';
import { InfoBox } from '@common/InfoBox';
import { PageHeader } from '@common/PageHeader';
import { convertCasesToLinks, downloadBlob, insertComponentsIntoText } from '@common/Utils';
import { BPButton } from '@/components/bp/bpbutton';
import { BPContainer } from '@/components/bp/bpcontainer';

export interface IEvidenceDetailsProps {
    id: number;
    labId: number;
    caseId: number;
}

export interface IEvidenceDetailsState {
    response: EvidenceDetailsResponseWithUrls;
}

export default class EvidenceDetails extends React.Component<IEvidenceDetailsProps, IEvidenceDetailsState> {
    client = new EvidenceClient();
    caseClient = new CaseClient();

    constructor(props: IEvidenceDetailsProps) {
        super(props);

        this.state = {
            response: null
        }
    }

    async componentDidMount() {
        let details = await this.client.getDetails(this.props.labId, this.props.caseId, this.props.id);
        this.setState({
            response: details,
        })
    }

    @areYouSure(() => { return { message: window.translation.evidenceView.deleteConfirm, title: window.translation.common.confirmDelete }; })
    deleteEvidence() {
        this.client.delete(this.props.labId, this.props.caseId, this.props.id)
    }

    async downloadEvidence() {
        let res = await this.client.download(this.props.labId, this.props.caseId, this.props.id)
        downloadBlob({ data: res.data, fileName: res.fileName });
    }

    public render() {
        if (this.state.response == null)
            return null;

        const { response: { createDeconvolutionUrl, editUrl, caseUrl, evidence } } = this.state;
        const evidenceCase = evidence.cases?.length > 0 ? evidence.cases[0] : null

        return (
            <BPContainer>
                <PageHeader
                    header={insertComponentsIntoText(window.translation.evidenceView.editTitle, {
                        '{evidenceName}': evidence.name,
                        '{caseName}': evidenceCase?.name ? <a key={evidence?.id} href={caseUrl}>{evidenceCase.name}</a> : '--'
                    })}
                    renderRight={() =>
                        <>
                            {evidence != null && evidence.active &&
                                <>
                                    {<Menu.Item fitted>
                                        {/* <BPButton as="a" href={createDeconvolutionUrl}>{window.translation.runType.deconvolution}</BPButton> */}
                                        {this.props.caseId && <BPButton as="a" href={buildUrl(AppRoutes.CaseRunCreate, { labId: this.props.labId, caseId: this.props.caseId, runType: 'deconv_main',  evidenceId: evidence.id })}>{window.translation.runType.deconvolution}</BPButton>}
                                        {!this.props.caseId && <BPButton as="a" onClick={async () => {
                                            let daily = await this.caseClient.createDaily(this.props.labId)
                                            let url = buildUrl(AppRoutes.CaseRunCreate, {
                                                labId: this.props.labId,
                                                caseId: daily.caseId,
                                                runType: 'deconv_main',
                                                evidenceId: evidence.id
                                            });
                                            window.location.assign(url);
                                        }}>{window.translation.runType.deconvolution}</BPButton>}
                                    </Menu.Item>}
                                    <Menu.Item fitted>
                                        <ButtonGroup>
                                            <BPButton as={Link} to={buildUrl(AppRoutes.EvidenceEdit, { labId: this.props.labId, caseId: this.props.caseId, evidenceId: evidence.id })}>Edit</BPButton>
                                            <BPButton as={Link} to={buildUrl(AppRoutes.EvidenceCreateCopy, { labId: this.props.labId, caseId: this.props.caseId, evidenceId: evidence.id })}>Create Copy</BPButton>
                                            <BPButton onClick={() => this.downloadEvidence()}>{window.translation.common.download}</BPButton>
                                            <BPButton negative onClick={() => this.deleteEvidence()}>{window.translation.common.delete}</BPButton>
                                        </ButtonGroup>
                                    </Menu.Item>
                                </>
                            }
                            {evidence != null && !evidence.active &&
                                <Message warning>
                                    <Header>{window.translation.evidenceView.deletedHeader}</Header>
                                    {window.translation.evidenceView.deletedMessage}
                                </Message>
                            }
                        </>
                    }></PageHeader>

                {evidence != null &&
                    <Grid>
                        <GridColumn width={16}>
                            <InfoBox header={window.translation.common.information} items={[
                                { label: window.translation.evidence.name, description: evidence.name },
                                { label: window.translation.common.kit, description: evidence.kitName },
                                { label: window.translation.common.created, description: evidence.created ? moment(evidence.created).format('l') : null },
                                { label: window.translation.common.owner, description: evidence.createdBy },
                                { label: window.translation.common.alleleFormat, description: toAlleleFormatName(evidence.alleleFormat) },
                                { showIf: evidence.cases?.length > 0, label: window.translation.common.cases, description: convertCasesToLinks(evidence.cases, this.props.labId) },
                            ]} />
                        </GridColumn>
                        <GridColumn width={16}>
                            <BPSection header={window.translation.kit.markers}>
                                <div className={'widetable'} style={{ 'maxHeight': '10000px' }}>
                                    <BPReactTable
                                        rowCssNameProvider={r => r.color}
                                        disablePagination
                                        disableGlobalFilter
                                        disableSortBy
                                        stickyHeader
                                        celled
                                        data={evidence.markers}
                                        columns={[
                                            { accessor: 'name', Header: window.translation.kit.marker },
                                        ].concat([...Array(evidence.maxAlleles).keys()].map(alleleIndex => {
                                            return [
                                                {
                                                    id: `al${alleleIndex}`,
                                                    accessor: 'alleles',
                                                    Header: window.translation.evidenceView.alleleWithNumber.replace('{alleleNumber}', (alleleIndex + 1).toString()),
                                                    Cell: cellProps => <span>{cellProps.value.length > alleleIndex ? cellProps.value[alleleIndex].allele : ''}</span>
                                                },
                                                {
                                                    id: `hi${alleleIndex}`,
                                                    accessor: 'alleles',
                                                    Header: window.translation.evidenceView.heightWithNumber.replace('{heightNumber}', (alleleIndex + 1).toString()),
                                                    Cell: cellProps => <span>{cellProps.value.length > alleleIndex ? cellProps.value[alleleIndex].height : ''}</span>
                                                }
                                            ]
                                        }).reduce((prev, curr) => { prev.push(curr[0]); prev.push(curr[1]); return prev; }, [])) as Array<BPColumn<EvidenceMarkerModel>>}
                                    />
                                </div>
                            </BPSection>
                        </GridColumn>
                        <GridColumn width={16}>
                            <BPSection header={window.translation.common.degradationPlot}>
                                {evidence.hasDegradation &&
                                    <div className="svgwrapper">
                                        <img src={evidence.degradationUrl} />
                                    </div>
                                }
                            </BPSection>
                        </GridColumn>
                        <GridColumn width={16}>
                            <BPSection header={window.translation.common.epg}>
                                {evidence.hasPlotlyEpg &&
                                    <BPPlotly jsonUrl={evidence.epgPlotlyUrl} title={window.translation.common.epg}></BPPlotly>
                                }
                            </BPSection>
                        </GridColumn>
                        {evidence.hasEpg &&
                            <GridColumn width={16}>
                                <BPSection header={window.translation.common.epg}>
                                    <div className="svgwrapper">
                                        <img src={evidence.epgOverlayUrl} />
                                    </div>
                                </BPSection>
                            </GridColumn>
                        }
                    </Grid>
                }


            </BPContainer>)
    }
}
